import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme } from '@mui/material';

const ThemeContext = createContext(null);

// ─── Brand Tokens ──────────────────────────────────────────────────
const brand = {
  navy: '#1A2B4A',
  navyLight: '#2E4A7A',
  gold: '#C8942A',
  goldLight: '#E8C878',
  copper: '#9E6B3B',
  parchment: '#F5F0E8',
  parchmentDark: '#E8E0D0',
  cream: '#FDFAF5',
  charcoal: '#2C2B28',
  charcoalMuted: '#5C5A55',
  error: '#8B2635',
  success: '#2D6A4F',
};

function buildTheme(mode) {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: { main: brand.navy, light: brand.navyLight },
      secondary: { main: brand.gold, light: brand.goldLight },
      error: { main: brand.error },
      warning: { main: brand.gold },
      success: { main: brand.success },
      info: { main: brand.navy },
      // Matches resume-optimizer design-system.css tokens
      background: {
        default: isDark ? '#0F1520' : brand.parchment,
        paper: isDark ? '#162035' : brand.cream,
      },
      text: {
        primary: isDark ? '#E8E0D0' : brand.charcoal,
        secondary: isDark ? '#B0A898' : brand.charcoalMuted,
      },
      divider: isDark ? '#2E4A7A' : brand.parchmentDark,
      action: {
        hover: isDark ? 'rgba(46,74,122,0.3)' : 'rgba(26,43,74,0.04)',
        selected: isDark ? 'rgba(46,74,122,0.5)' : 'rgba(26,43,74,0.08)',
      }
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      h1: { fontFamily: "'Playfair Display', serif" },
      h2: { fontFamily: "'EB Garamond', serif" },
      h3: { fontFamily: "'EB Garamond', serif" },
      h4: { fontFamily: "'EB Garamond', serif" },
      h5: { fontFamily: "'EB Garamond', serif" },
      h6: { fontFamily: "'EB Garamond', serif" },
      // Semantic font size tokens
      fontSizeMonoId: '0.65rem',       // truncated UUIDs, entity IDs
      fontSizeMonoBlock: '0.75rem',    // JSON display, code blocks
      fontSizeChipSmall: '0.65rem',    // small chip labels
      fontSizeChipMedium: '0.7rem',    // medium chip labels
      fontSizeTableCell: '0.8rem',     // table body cell text
      fontSizeAdminLabel: '0.65rem',   // sidebar section labels
      fontSizeMetaCaption: '0.6rem',   // smallest meta-text, timestamps
      fontSizeNavItem: '0.85rem',      // navigation button text
      monoFontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
      button: {
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        letterSpacing: '0.05em',
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          outlined: {
            borderColor: isDark ? '#2E4A7A' : brand.parchmentDark,
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isDark ? '#1E2D4A' : brand.parchment,
              color: isDark ? '#B0A898' : brand.charcoalMuted,
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 4,
          }
        }
      }
    }
  });
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('billing-theme') || 'light';
  });

  useEffect(() => {
    localStorage.setItem('billing-theme', mode);
  }, [mode]);

  const toggleMode = () => setMode(prev => prev === 'light' ? 'dark' : 'light');

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ mode, isDark: mode === 'dark', toggleMode, theme, brand }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeContext);
}
